import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronRightIcon,
  ClipboardIcon,
  DocumentArrowUpIcon,
  LightBulbIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAppState } from '../hooks/useAppState';
import DocumentAnalysis from '../pages/DurableDocumentAnalysis';
import DurableOutlineWorkspace from '../pages/DurableOutlineWorkspace';
import ContentEdit from '../pages/DurableContentWorkspace';
import { OutlineData, OutlineItem } from '../types';
import {
  configApi,
  expandApi,
  FileUploadResponse,
  getErrorMessage,
  listSnapshotApi,
  outlineApi,
  projectFileApi,
  ProjectDetail,
  ProjectSettings,
  ProjectSummary,
  projectApi,
  analysisApi,
  readSseStream,
} from '../services/api';
import { draftStorage } from '../utils/draftStorage';

interface WorkflowStudioProps {
  project: ProjectSummary | null;
  routeStep?: number;
  onBack: () => void;
  onProjectCreated: (project: ProjectSummary) => void;
  onRouteChange?: (step: number, projectId?: number | null) => void;
}

interface ProjectFormState extends ProjectSettings {
  projectName: string;
  expectedTotalWords: number;
  versionNo: number;
  company_scale: string;
  expected_pages: number;
  layout_style: string;
  supplier_role: string;
  planned_duration: string;
  plan_duration_days: string;
  plan_duration_text: string;
}

interface ReferenceMaterial {
  fileName: string;
  oldOutline?: string;
  oldDocument?: string;
}

type AiModalType = 'optimize' | 'expand' | 'manual';

const PRESERVE_EXISTING_PATTERN = /保留|不要删除|不得删除|不可删除/;

const preserveMissingRootItems = (
  existingItems: OutlineItem[],
  generatedItems: OutlineItem[]
): OutlineItem[] => {
  const generatedIds = new Set(generatedItems.map((item) => item.id));
  const generatedTitles = new Set(
    generatedItems.map((item) => item.title.trim())
  );
  const missingItems = existingItems.filter(
    (item) => !generatedIds.has(item.id) && !generatedTitles.has(item.title.trim())
  );
  return [...generatedItems, ...missingItems];
};

const STEPS = [
  { step: 0, title: '标书设置', description: '设置标书信息与写作偏好' },
  { step: 1, title: '招标文件解读', description: '解析招标要求与评分标准' },
  { step: 2, title: '目录编写', description: '生成和调整目录结构' },
  { step: 3, title: '正文编写', description: '批量生成并导出标书' },
];

const flowStatusToStep = (flowStatus: number) => {
  if (flowStatus >= 30) return 3;
  if (flowStatus >= 20) return 2;
  if (flowStatus >= 10) return 1;
  return 0;
};

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required }) => (
  <span className="mb-2 block text-sm font-medium text-slate-700">
    {children}
    {required && <span className="ml-1 text-red-500">*</span>}
  </span>
);

const SegmentedOptions: React.FC<{
  value: string;
  options: string[];
  onChange: (value: string) => void;
  columns?: number;
}> = ({ value, options, onChange, columns = 3 }) => (
  <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
    {options.map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange(option)}
        className={`rounded-xl border px-4 py-3 text-sm transition ${
          value === option
            ? 'border-[var(--bid-color-primary)] bg-[var(--bid-color-primary-soft)] font-medium text-[var(--bid-color-primary)]'
            : 'border-slate-200 bg-white text-slate-600 hover:border-[var(--bid-color-primary-border)]'
        }`}
      >
        {option}
      </button>
    ))}
  </div>
);

const UploadCard: React.FC<{
  title: string;
  description: string;
  uploadHint: string;
  pasteHint?: string;
  acceptFormats?: string;
  maxSize?: string;
  showPaste?: boolean;
  fileRole?: string;
  onUpload?: (file: File) => void;
  onPaste?: (text: string) => void;
  onBlur?: (text: string) => void;
  uploadMode?: 'upload' | 'paste';
  onModeChange?: (mode: 'upload' | 'paste') => void;
  fileName?: string;
  isUploading?: boolean;
}> = ({
  title,
  description,
  uploadHint,
  pasteHint,
  acceptFormats,
  maxSize,
  showPaste = true,
  onUpload,
  onPaste,
  onBlur,
  uploadMode = 'upload',
  onModeChange,
  fileName,
  isUploading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pasteText, setPasteText] = useState('');
  const [showPasteArea, setShowPasteArea] = useState(uploadMode === 'paste');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
    }
  };

  const handlePasteSubmit = () => {
    if (pasteText.trim() && onPaste) {
      onPaste(pasteText);
      setPasteText('');
    }
  };

  return (
    <div>
      <div className={showPaste ? 'grid grid-cols-2 gap-3' : ''}>
        {!showPaste ? (
          <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-4 text-center transition hover:border-[var(--bid-color-primary-border)] hover:bg-[var(--bid-color-primary-soft)]">
            <DocumentArrowUpIcon className="h-6 w-6 text-[var(--bid-color-primary)]" />
            <div className="text-sm font-medium text-slate-700">
              {isUploading ? '正在上传...' : fileName || `上传${title}`}
            </div>
            <div className="text-xs text-slate-400">
              {uploadHint}
            </div>
            {acceptFormats && (
              <div className="mt-1 text-xs text-slate-400">
                {acceptFormats}{maxSize ? `，文件最大${maxSize}` : ''}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              disabled={isUploading}
              onChange={handleFileChange}
            />
          </label>
        ) : (
          <>
            <label
              className={`flex min-h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border px-6 py-4 text-center transition ${
                !showPasteArea
                  ? 'border-[var(--bid-color-primary)] bg-[var(--bid-color-primary-soft)]'
                  : 'border-slate-200 bg-white hover:border-[var(--bid-color-primary-border)]'
              }`}
            >
              <DocumentArrowUpIcon className={`h-6 w-6 ${!showPasteArea ? 'text-[var(--bid-color-primary)]' : 'text-slate-400'}`} />
              {/* paste 模式下左侧始终显示"上传"，不显示粘贴文件名，避免混淆 */}
              <div className={`text-sm font-medium ${!showPasteArea ? 'text-[var(--bid-color-primary)]' : 'text-slate-700'}`}>
                {isUploading ? '正在上传...' : (!showPasteArea && fileName ? fileName : `上传${title}`)}
              </div>
              <div className="text-xs text-slate-400">{uploadHint}</div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                disabled={isUploading}
                onChange={(e) => {
                  handleFileChange(e);
                  setShowPasteArea(false);
                  onModeChange?.('upload');
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => { setShowPasteArea(true); onModeChange?.('paste'); }}
              className={`flex min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border px-6 py-4 text-center transition ${
                showPasteArea
                  ? 'border-[var(--bid-color-primary)] bg-[var(--bid-color-primary-soft)]'
                  : 'border-slate-200 bg-white hover:border-[var(--bid-color-primary-border)]'
              }`}
            >
              <ClipboardIcon className={`h-6 w-6 ${showPasteArea ? 'text-[var(--bid-color-primary)]' : 'text-slate-400'}`} />
              <div className={`text-sm font-medium ${showPasteArea ? 'text-[var(--bid-color-primary)]' : 'text-slate-700'}`}>
                {pasteHint || `粘贴${title}`}
              </div>
              <div className="text-xs text-slate-400">直接粘贴，无需调整格式</div>
            </button>
          </>
        )}
      </div>

      {showPasteArea && showPaste && (
        <div className="mt-3">
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            onBlur={() => {
              if (pasteText.trim() && onBlur) {
                onBlur(pasteText);
              }
            }}
            placeholder={`请粘贴${title}内容...`}
            className="min-h-40 w-full rounded-xl border border-slate-200 p-4 text-sm outline-none focus:border-[var(--bid-color-primary)]"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={handlePasteSubmit}
              disabled={!pasteText.trim()}
              className="rounded-lg bg-[var(--bid-color-primary)] px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              保存
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectSetup: React.FC<{
  initial: ProjectFormState;
  projectId: number | null;
  referenceName: string;
  isSaving: boolean;
  message: string;
  onReferenceReady: (material: ReferenceMaterial) => void;
  onSave: (form: ProjectFormState, referenceFile: File | null) => Promise<number | undefined>;
}> = ({
  initial,
  projectId,
  referenceName: initialReferenceName,
  isSaving,
  message,
  onReferenceReady,
  onSave,
}) => {
  const [form, setForm] = useState<ProjectFormState>(initial);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [referenceName, setReferenceName] = useState(initialReferenceName);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);

  // 可用正文风格（从后端 /api/config/content-styles 读，和正文编写页保持一致）
  const [availableStyles, setAvailableStyles] = useState<string[]>([
    '通用', '实用', '一二级显示摘要', '前三级显示摘要', '每一级都是总分结构',
  ]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await configApi.getAvailableContentStyles();
        if (!cancelled && data?.data?.length) {
          setAvailableStyles(data.data.map((s) => s.key));
        }
      } catch {
        // 接口失败时保留默认值即可
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  useEffect(() => {
    setReferenceName(initialReferenceName);
  }, [initialReferenceName]);

  const update = <K extends keyof ProjectFormState>(
    key: K,
    value: ProjectFormState[K]
  ) => setForm((previous) => ({ ...previous, [key]: value }));

  const uploadReference = async (file: File) => {
    setReferenceFile(file);
    setReferenceName(file.name);
    if (!projectId) return;

    try {
      const response = await expandApi.uploadExpandFile(file, projectId);
      onReferenceReady({
        fileName: file.name,
        oldOutline: response.data.data.old_outline,
        oldDocument: response.data.data.file_content,
      });
      setReferenceFile(null);
    } catch (error) {
      console.error('参考文件上传失败:', error);
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-7">
        <h2 className="text-2xl font-semibold text-slate-900">标书设置</h2>
        <p className="mt-2 text-sm text-slate-500">填写基础信息，开始创建你的标书</p>
      </div>

      <div className="space-y-6">
        <section className="rounded-2xl border border-slate-100 bg-white p-7 shadow-[0_8px_30px_rgba(31,52,86,0.04)]">
          <div className="mb-6 flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--bid-color-primary-soft)] text-sm font-semibold text-[var(--bid-color-primary)]">1</span>
            <div>
              <h3 className="font-semibold text-slate-900">基础信息</h3>
              <p className="mt-1 text-xs text-slate-400">必填项，用于创建项目</p>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <label className="block md:col-span-2">
              <FieldLabel required>项目名称</FieldLabel>
              <input
                value={form.projectName}
                onChange={(event) => update('projectName', event.target.value.slice(0, 100))}
                placeholder="例如：智慧园区信息化建设项目"
                className="h-12 w-full rounded-xl border-slate-200 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
              />
            </label>

            <label className="block">
              <FieldLabel required>目标总字数</FieldLabel>
              <div className="relative">
                <input
                  type="number"
                  min={1}
                  value={form.expectedTotalWords}
                  onChange={(event) => update('expectedTotalWords', Math.max(1, Number(event.target.value)))}
                  className="h-12 w-full rounded-xl border-slate-200 pr-12 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">字</span>
              </div>
            </label>

            <label className="block">
              <FieldLabel>标书类型</FieldLabel>
              <SegmentedOptions
                value={form.tender_type || '服务类'}
                options={['服务类', '货物类', '工程类']}
                onChange={(value) => update('tender_type', value)}
              />
            </label>
          </div>
        </section>

        {/* 高级设置 - 折叠展开 */}
        <section className="rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(31,52,86,0.04)]">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex w-full items-center justify-between p-5 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600">2</span>
              <div>
                <h3 className="font-semibold text-slate-900">高级设置</h3>
                <p className="mt-1 text-xs text-slate-400">可选配置，帮助 AI 生成更精准的内容</p>
              </div>
            </div>
            <ChevronRightIcon className={`h-5 w-5 text-slate-400 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
          </button>

          {showAdvanced && (
            <div className="border-t border-slate-100 p-7">
              <div className="mb-6">
                <h4 className="mb-3 text-sm font-medium text-slate-700">企业信息</h4>
                <div className="grid gap-5 md:grid-cols-2">
                  <label className="block">
                    <FieldLabel>公司名称</FieldLabel>
                    <input
                      value={form.company_name || ''}
                      onChange={(event) => update('company_name', event.target.value)}
                      placeholder="请输入投标公司名称"
                      className="h-12 w-full rounded-xl border-slate-200 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                    />
                  </label>
                  <label className="block">
                    <FieldLabel>所属行业</FieldLabel>
                    <input
                      value={form.company_industry || ''}
                      onChange={(event) => update('company_industry', event.target.value)}
                      placeholder="例如：软件和信息技术服务业"
                      className="h-12 w-full rounded-xl border-slate-200 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                    />
                  </label>
                  <label className="block">
                    <FieldLabel>公司规模</FieldLabel>
                    <input
                      value={form.company_scale}
                      onChange={(event) => update('company_scale', event.target.value)}
                      placeholder="例如：100-500人"
                      className="h-12 w-full rounded-xl border-slate-200 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                    />
                  </label>
                  <label className="block">
                    <FieldLabel>预设页数</FieldLabel>
                    <div className="relative">
                      <input
                        type="number"
                        min={1}
                        value={form.expected_pages || ''}
                        onChange={(event) => update('expected_pages', Math.max(0, Number(event.target.value)))}
                        placeholder="可选"
                        className="h-12 w-full rounded-xl border-slate-200 pr-12 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">页</span>
                    </div>
                  </label>
                  <label className="block md:col-span-2">
                    <FieldLabel>公司简介</FieldLabel>
                    <textarea
                      value={form.company_intro || ''}
                      onChange={(event) => update('company_intro', event.target.value.slice(0, 200))}
                      rows={3}
                      placeholder="简要介绍企业资质、团队和项目经验"
                      className="w-full rounded-xl border-slate-200 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                    />
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="mb-3 text-sm font-medium text-slate-700">写作偏好</h4>
                <div className="space-y-4">
                  <div>
                    <FieldLabel>写作风格</FieldLabel>
                    <SegmentedOptions
                      value={form.writing_style || '通用'}
                      options={availableStyles}
                      onChange={(value) => update('writing_style', value)}
                    />
                  </div>
                  <div>
                    <FieldLabel>表格使用</FieldLabel>
                    <SegmentedOptions
                      value={form.table_density || '适量'}
                      options={['无', '少量', '适量']}
                      onChange={(value) => update('table_density', value)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-3 text-sm font-medium text-slate-700">参考文件</h4>
                <UploadCard
                  title="上传参考文件"
                  description=""
                  uploadHint="上传历史标书、范本等作为参考（可选）"
                  acceptFormats="文件格式:doc、docx、pdf"
                  maxSize="50MB"
                  fileName={referenceName}
                  showPaste={false}
                  onUpload={uploadReference}
                />
              </div>
            </div>
          )}
        </section>
      </div>

      {message && (
        <div className={`mt-5 rounded-xl px-4 py-3 text-sm ${
          message.includes('成功') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
        }`}>
          {message}
        </div>
      )}

      <div className="mt-7 flex justify-end">
        <button
          onClick={async () => {
            const newVersion = await onSave(form, referenceFile);
            if (newVersion !== undefined) {
              update('versionNo', newVersion);
            }
          }}
          disabled={isSaving || !form.projectName.trim()}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-[var(--bid-color-primary)] px-6 text-sm font-medium text-white shadow-lg shadow-violet-100 hover:bg-[var(--bid-color-primary-hover)] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
        >
          {isSaving ? '保存中...' : '下一步'}
          {!isSaving && <ArrowRightIcon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const AiDirectoryModal: React.FC<{
  type: AiModalType;
  outlineTitles: string[];
  outlineData: OutlineData;
  projectOverview: string;
  techRequirements: string;
  onAdopt: (outline: OutlineData) => void;
  onClose: () => void;
}> = ({
  type,
  outlineTitles,
  outlineData,
  projectOverview,
  techRequirements,
  onAdopt,
  onClose,
}) => {
  const [instruction, setInstruction] = useState('');
  const [style, setStyle] = useState('专业严谨');
  const [preview, setPreview] = useState<OutlineData | null>(null);
  const [generating, setGenerating] = useState(false);
  const [progressLogs, setProgressLogs] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const titles = {
    optimize: '智能优化目录',
    expand: '智能扩写目录',
    manual: '手动编写思路',
  };
  const defaultInstructions = {
    optimize: '优化现有目录的完整性、层级关系和评分要求覆盖度，删除重复表达。',
    expand: '保持现有目录主线不变，补充缺失的二级和三级目录。',
    manual: '按照用户提供的编写思路调整现有目录。',
  };
  const previewTitles = useMemo(() => {
    const collect = (items: OutlineItem[]): string[] =>
      items.flatMap((item) => [
        `${item.id} ${item.title}`,
        ...(item.children ? collect(item.children) : []),
      ]);
    return preview ? collect(preview.outline) : [];
  }, [preview]);

  const generatePreview = async () => {
    setGenerating(true);
    setPreview(null);
    setProgressLogs([]);
    setErrorMessage('');
    try {
      const extraInstruction = instruction.trim();
      const response = await outlineApi.generateOutlineStream({
        overview: projectOverview,
        requirements: techRequirements,
        mode: outlineData.outline.some((item) => item.source_requirement_id)
          ? 'aligned'
          : 'free',
        uploaded_expand: true,
        old_outline: JSON.stringify({ outline: outlineData.outline }),
        optimization_instruction: extraInstruction
          ? `${defaultInstructions[type]} 补充要求：${extraInstruction}`
          : defaultInstructions[type],
        optimization_style: style,
      });

      let result: OutlineData | null = null;
      await readSseStream(
        response,
        (event) => {
          if (event.error) {
            throw new Error(event.message || '目录优化失败');
          }
          if (event.type === 'progress' && event.message) {
            setProgressLogs((previous) => [...previous, event.message || '']);
          }
          if (event.type === 'result' && event.outline) {
            const received = event.outline;
            const receivedItems = received.outline
              ?? (received as unknown as OutlineItem[]);
            const shouldPreserve = PRESERVE_EXISTING_PATTERN.test(extraInstruction);
            const mergedItems = shouldPreserve
              ? preserveMissingRootItems(outlineData.outline, receivedItems)
              : receivedItems;
            result = {
              ...outlineData,
              outline: mergedItems,
            };
            // #region debug-point C:outline-preservation
            if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
              void fetch('http://127.0.0.1:7780/event', {
                method: 'POST',
                body: JSON.stringify({
                  sessionId: 'regression-fixes-validation',
                  runId: 'post-fix',
                  hypothesisId: 'C',
                  location: 'frontend/src/visual/WorkflowStudio.tsx:optimize-outline',
                  msg: '[DEBUG] Smart outline root items merged',
                  data: {
                    shouldPreserve,
                    existingCount: outlineData.outline.length,
                    receivedCount: receivedItems.length,
                    mergedCount: mergedItems.length,
                  },
                  ts: Date.now(),
                }),
              }).catch(() => undefined);
            }
            // #endregion
          }
        },
        '目录优化失败'
      );

      if (!result) {
        throw new Error('未收到目录优化结果');
      }
      setPreview(result);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, '目录优化失败'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-slate-950/45 p-6 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-7 py-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{titles[type]}</h2>
            <p className="mt-1 text-xs text-slate-400">当前目录 / 选择内容 / 生成预览</p>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100" aria-label="关闭">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid min-h-[520px] flex-1 grid-cols-[1fr_64px_1fr] overflow-hidden p-7">
          <div className="flex min-h-0 flex-col rounded-2xl border border-slate-200">
            <div className="border-b border-slate-100 px-5 py-4 text-sm font-medium text-slate-800">当前目录内容</div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
              {outlineTitles.length ? outlineTitles.map((title, index) => (
                <div key={`${title}-${index}`} className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  {index + 1}. {title}
                </div>
              )) : (
                <div className="grid h-full place-items-center text-sm text-slate-400">请先生成目录</div>
              )}
            </div>
            <div className="space-y-4 border-t border-slate-100 p-5">
              <label className="block">
                <FieldLabel>{type === 'manual' ? '编写思路' : '补充指令'}</FieldLabel>
                <textarea
                  value={instruction}
                  onChange={(event) => setInstruction(event.target.value)}
                  rows={3}
                  placeholder="描述希望调整的方向和重点"
                  className="w-full rounded-xl border-slate-200 text-sm focus:border-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
                />
              </label>
              <label className="block">
                <FieldLabel>生成风格</FieldLabel>
                <select
                  value={style}
                  onChange={(event) => setStyle(event.target.value)}
                  className="h-11 w-full rounded-xl border-slate-200 text-sm"
                >
                  <option>专业严谨</option>
                  <option>结构清晰</option>
                  <option>简洁凝练</option>
                </select>
              </label>
            </div>
          </div>

          <div className="grid place-items-center">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-[var(--bid-color-primary-soft)] text-[var(--bid-color-primary)]">
              <ChevronRightIcon className="h-6 w-6" />
            </div>
          </div>

          <div className="flex min-h-0 flex-col rounded-2xl border border-[var(--bid-color-primary-soft)] bg-[var(--bid-color-primary-soft)]/30">
            <div className="flex items-center gap-2 border-b border-blue-100 px-5 py-4 text-sm font-medium text-slate-800">
              <SparklesIcon className="h-4 w-4 text-[var(--bid-color-primary)]" />
              智能生成结果预览
            </div>
            <div className={`${preview ? 'overflow-y-auto' : 'grid place-items-center text-center'} flex-1 p-8`}>
              {preview ? (
                <div className="space-y-3">
                  {previewTitles.map((title, index) => (
                    <div key={`${title}-${index}`} className="rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm text-slate-700">
                      {title}
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                <LightBulbIcon className="mx-auto h-12 w-12 text-[var(--bid-color-primary-border)]" />
                <h3 className="mt-4 font-medium text-slate-700">等待生成结果</h3>
                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  输入补充要求后生成真实目录预览，采用前不会覆盖当前目录。
                </p>
                </div>
              )}
            </div>
            {progressLogs.length > 0 && (
              <div className="max-h-24 overflow-y-auto border-t border-blue-100 bg-white/70 px-5 py-3 text-xs text-slate-500">
                {progressLogs[progressLogs.length - 1]}
              </div>
            )}
            {errorMessage && (
              <div className="border-t border-red-100 bg-red-50 px-5 py-3 text-sm text-red-600">{errorMessage}</div>
            )}
            <div className="flex justify-end gap-3 border-t border-blue-100 p-5">
              <button onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm text-slate-600">取消</button>
              <button
                onClick={generatePreview}
                disabled={generating || (type === 'manual' && !instruction.trim())}
                className="rounded-xl bg-[var(--bid-color-primary)] px-5 py-2.5 text-sm font-medium text-white disabled:bg-slate-300"
              >
                {generating ? '生成中...' : '生成预览'}
              </button>
              <button
                onClick={() => preview && onAdopt(preview)}
                disabled={!preview || generating}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white disabled:bg-slate-200 disabled:text-slate-400"
              >
                采用结果
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TechRequirementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  overview: string;
  requirements: string;
  onSave: (overview: string, requirements: string) => void;
}

const TechRequirementsModal: React.FC<TechRequirementsModalProps> = ({
  isOpen,
  onClose,
  overview: initialOverview,
  requirements: initialRequirements,
  onSave,
}) => {
  const [overview, setOverview] = useState(initialOverview);
  const [requirements, setRequirements] = useState(initialRequirements);

  useEffect(() => {
    setOverview(initialOverview);
    setRequirements(initialRequirements);
  }, [initialOverview, initialRequirements]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(overview, requirements);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-slate-950/45 p-6 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-7 py-5">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">粘贴项目信息</h2>
            <p className="mt-1 text-sm text-slate-500">手动粘贴并保存项目概述与技术评分要求</p>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100" aria-label="关闭">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="grid gap-6 p-7 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">项目概述*</label>
            <textarea
              value={overview}
              onChange={(e) => setOverview(e.target.value)}
              placeholder="请粘贴或填写项目概述"
              className="min-h-48 w-full rounded-xl border border-slate-200 p-4 text-sm outline-none focus:border-[var(--bid-color-primary)]"
            />
            <div className="mt-1 text-right text-xs text-slate-400">{overview.length}/30000</div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">技术部分评分要求*</label>
            <textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              placeholder="请粘贴或填写技术评分要求"
              className="min-h-48 w-full rounded-xl border border-slate-200 p-4 text-sm outline-none focus:border-[var(--bid-color-primary)]"
            />
            <div className="mt-1 text-right text-xs text-slate-400">{requirements.length}/30000</div>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-100 px-7 py-5">
          <button onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm text-slate-600">取消</button>
          <button
            onClick={handleSave}
            className="rounded-xl bg-[var(--bid-color-primary)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--bid-color-primary-hover)]"
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkflowStudio: React.FC<WorkflowStudioProps> = ({
  project,
  routeStep,
  onBack,
  onProjectCreated,
  onRouteChange,
}) => {
  const {
    state,
    updateFileContent,
    updateAnalysisResults,
    updateOutline,
  } = useAppState();
  const savedStep = draftStorage.loadDraft()?.currentStep;
  const [currentStep, setCurrentStep] = useState(() =>
    typeof routeStep === 'number' && routeStep >= 0 && routeStep <= 3
      ? routeStep
      : typeof savedStep === 'number' && savedStep >= 0 && savedStep <= 3
      ? savedStep
      : project
        ? flowStatusToStep(project.flow_status)
        : 0
  );
  const [projectId, setProjectId] = useState<number | null>(project?.id || null);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [referenceMaterial, setReferenceMaterial] = useState<ReferenceMaterial | null>(null);
  const [settings, setSettings] = useState<ProjectFormState>({
    projectName: project?.project_name || '',
    expectedTotalWords: 5000,
    versionNo: 0,
    tender_type: '服务类',
    writing_style: '通用',
    table_density: '适量',
    company_name: '',
    company_intro: '',
    company_industry: '',
    bid_focus: '',
    company_scale: '',
    expected_pages: 8,
    layout_style: '',
    supplier_role: '',
    planned_duration: '',
    plan_duration_days: '',
    plan_duration_text: '',
  });
  const [isLoading, setIsLoading] = useState(Boolean(project));
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [aiModal, setAiModal] = useState<AiModalType | null>(null);
  const [confirmedAnalysisVersion, setConfirmedAnalysisVersion] = useState(0);
  const [showTechRequirementsModal, setShowTechRequirementsModal] = useState(false);
  const [listFile, setListFile] = useState<{ name: string; role: string } | null>(null);
  const [listUploadMode, setListUploadMode] = useState<'upload' | 'paste'>('upload');
  const [listPasteText, setListPasteText] = useState('');
  const [templateFile, setTemplateFile] = useState<{ name: string } | null>(null);
  const [writingMaterialFile, setWritingMaterialFile] = useState<{ name: string } | null>(null);
  const [drawingFile, setDrawingFile] = useState<{ name: string } | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const tenderDocFileRef = useRef<HTMLInputElement>(null);
  const listSaveDedupRef = useRef<{ text: string; ts: number } | null>(null);
  const cachedListVersionRef = useRef<number>(0);
  const continueInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    if (project?.id && projectId !== project.id) {
      setProjectId(project.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const moveToStep = (step: number, activeProjectId = projectId) => {
    setCurrentStep(step);
    draftStorage.saveDraft({ currentStep: step });
    onRouteChange?.(step, activeProjectId);
  };

  useEffect(() => {
    if (typeof routeStep === 'number' && routeStep >= 0 && routeStep <= 3) {
      setCurrentStep(routeStep);
    }
  }, [routeStep]);

  const rememberReference = (
    fileName: string,
    response: FileUploadResponse
  ) => {
    setReferenceMaterial({
      fileName,
      oldOutline: response.old_outline,
      oldDocument: response.file_content,
    });
  };

  useEffect(() => {
    if (!project) return;
    let isActive = true;
    const loadStartedAt = performance.now();
    setIsLoading(true);

    Promise.all([
      projectApi.detail(project.id),
      projectApi.settingsDetail(project.id),
      project.flow_status >= 30 ? projectApi.getOutline(project.id) : Promise.resolve(null),
    ])
      .then(([detailResponse, settingsResponse, outlineResponse]) => {
        if (!isActive) return;
        const loadedDetail = detailResponse.data.data;
        const loadedSettings = settingsResponse.data.data;
        setDetail(loadedDetail);
        setSettings({
          projectName: loadedDetail.project_name,
          expectedTotalWords: loadedSettings.expected_total_words || 5000,
          versionNo: loadedSettings.version_no,
          tender_type: loadedSettings.tender_type || '服务类',
          writing_style: loadedSettings.writing_style || '通用',
          table_density: loadedSettings.table_density || '适量',
          company_name: loadedSettings.company_name || '',
          company_intro: loadedSettings.company_intro || '',
          company_industry: loadedSettings.company_industry || '',
          bid_focus: loadedSettings.bid_focus || '',
          company_scale: loadedSettings.company_scale || '',
          expected_pages: loadedSettings.expected_pages || 8,
          layout_style: loadedSettings.layout_style || '',
          supplier_role: loadedSettings.supplier_role || '',
          planned_duration: loadedSettings.planned_duration || '',
          plan_duration_days: loadedSettings.plan_duration_days?.toString() || '',
          plan_duration_text: loadedSettings.plan_duration_text || '',
        });
        const files = loadedDetail.files;
        const listFileEntry = files?.list?.[0];
        if (listFileEntry) {
          setListFile({ name: listFileEntry.file_name, role: 'list' });
        }
        const templateEntry = files?.template?.[0];
        if (templateEntry) {
          setTemplateFile({ name: templateEntry.file_name });
        }
        const writingEntry = files?.writing_material?.[0];
        if (writingEntry) {
          setWritingMaterialFile({ name: writingEntry.file_name });
        }
        const drawingEntry = files?.drawing?.[0];
        if (drawingEntry) {
          setDrawingFile({ name: drawingEntry.file_name });
        }
        updateAnalysisResults(loadedDetail.overview || '', loadedDetail.requirements || '');
        // #region debug-point A:project-detail-loaded
        if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
          void fetch('http://127.0.0.1:7783/event', {
            method: 'POST',
            body: JSON.stringify({
              sessionId: 'export-project-name',
              runId: 'post-fix',
              hypothesisId: 'A',
              location: 'frontend/src/visual/WorkflowStudio.tsx:load-project',
              msg: '[DEBUG] Project detail loaded before outline update',
              data: {
                projectId: project.id,
                projectFlowStatus: project.flow_status,
                detailProjectName: loadedDetail.project_name || '',
                outlineRequested: Boolean(outlineResponse),
                outlineCount: outlineResponse?.data.data.length || 0,
              },
              ts: Date.now(),
            }),
          }).catch(() => undefined);
        }
        // #endregion
        if (outlineResponse) {
          updateOutline({
            project_name: loadedDetail.project_name,
            project_overview: loadedDetail.overview || '',
            outline: outlineResponse.data.data,
          });
        }
        // 如果项目已有分析结果，自动设置 confirmedAnalysisVersion 以启用"保存并生成目录"按钮
        const hasAnalysis = !!(loadedDetail.overview?.trim() || loadedDetail.requirements?.trim());
        if (hasAnalysis) {
          const version = loadedDetail.analysis_version || 1;
          setConfirmedAnalysisVersion(version);
        }
        // #region debug-point D:project-core-loaded
        if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
          void fetch('http://127.0.0.1:7780/event', {
            method: 'POST',
            body: JSON.stringify({
              sessionId: 'regression-fixes-validation',
              runId: 'post-fix',
              hypothesisId: 'D',
              location: 'frontend/src/visual/WorkflowStudio.tsx:load-project',
              msg: '[DEBUG] Project core data loaded',
              data: {
                projectId: project.id,
                elapsedMs: Math.round(performance.now() - loadStartedAt),
              },
              ts: Date.now(),
            }),
          }).catch(() => undefined);
        }
        // #endregion
        const fileLoads: Promise<unknown>[] = [];
        const latestTender = loadedDetail.files.tender[0];
        if (latestTender) {
          fileLoads.push(
            projectApi.getFileContent(project.id, latestTender.id).then((response) => {
              if (isActive) updateFileContent(response.data.data.content);
            })
          );
        }
        const latestReference = loadedDetail.files.expand[0];
        if (latestReference) {
          fileLoads.push(
            projectApi.getFileContent(project.id, latestReference.id).then((response) => {
              if (isActive) {
                setReferenceMaterial({
                  fileName: response.data.data.file_name,
                  oldDocument: response.data.data.content,
                });
              }
            })
          );
        }
        void Promise.all(fileLoads)
          .then(() => {
            // #region debug-point D:historical-content-loaded
            if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
              void fetch('http://127.0.0.1:7780/event', {
                method: 'POST',
                body: JSON.stringify({
                  sessionId: 'regression-fixes-validation',
                  runId: 'post-fix',
                  hypothesisId: 'D',
                  location: 'frontend/src/visual/WorkflowStudio.tsx:load-history',
                  msg: '[DEBUG] Historical file content loaded',
                  data: {
                    projectId: project.id,
                    fileRequestCount: fileLoads.length,
                    elapsedMs: Math.round(performance.now() - loadStartedAt),
                  },
                  ts: Date.now(),
                }),
              }).catch(() => undefined);
            }
            // #endregion
          })
          .catch((requestError) => {
            if (isActive) {
              setMessage(getErrorMessage(requestError, '历史文件正文加载失败'));
            }
          });
      })
      .catch((requestError) => {
        if (isActive) setMessage(getErrorMessage(requestError, '项目数据加载失败'));
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [project, updateAnalysisResults, updateFileContent, updateOutline]);

  const saveUploadedFile = async (content: string) => {
    if (projectId) {
      await projectApi.updateFlowStatus(projectId, 10);
      const response = await projectApi.detail(projectId);
      setDetail(response.data.data);
    }
    updateFileContent(content);
  };

  const removeProjectFile = (fileId: number) => {
    setDetail((previous) => previous ? {
      ...previous,
      files: {
        ...previous.files,
        tender: previous.files.tender.filter((file) => file.id !== fileId),
      },
    } : previous);
  };

  const getListTitle = (type?: string | null): string => {
    switch (type) {
      case '货物类': return '货物清单';
      case '工程类': return '工程清单';
      default: return '服务清单';
    }
  };

  const saveListToBackend = async (text?: string) => {
    if (!projectId) return;
    const content = text ?? listPasteText;
    if (!content.trim()) {
      setMessage('请先粘贴清单内容');
      return;
    }

    const now = Date.now();
    if (
      listSaveDedupRef.current &&
      listSaveDedupRef.current.text === content &&
      now - listSaveDedupRef.current.ts < 1000
    ) {
      return;
    }
    listSaveDedupRef.current = { text: content, ts: now };

    try {
      let baseAnalysisVersion = cachedListVersionRef.current;
      try {
        const snapshotResp = await listSnapshotApi.get(projectId);
        const v = snapshotResp.data?.data?.analysis_version;
        if (v != null) {
          baseAnalysisVersion = v;
          cachedListVersionRef.current = v;
        }
      } catch {
        // ignore, use cached
      }

      const idempotencyKey = `list-snapshot-${projectId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const resp = await listSnapshotApi.saveFromPasted(projectId, {
        base_analysis_version: baseAnalysisVersion,
        pasted_text: content,
        idempotency_key: idempotencyKey,
      });
      const newVersion = resp.data?.data?.analysis_version;
      if (newVersion != null) {
        cachedListVersionRef.current = newVersion;
      }
      setMessage('清单保存成功');
    } catch (error) {
      const errorMsg = getErrorMessage(error, '清单保存失败');
      setMessage(errorMsg);
    }
  };

  const uploadStep2File = async (
    file: File,
    role: 'list' | 'template' | 'writing_material' | 'reference_bid' | 'drawing'
  ) => {
    if (!projectId) {
      setMessage('请先保存项目再上传文件');
      return;
    }
    setUploadingFile(true);
    setMessage('');
    try {
      await projectFileApi.upload(projectId, file, role, `step2-${Date.now()}`);
      if (role === 'list') {
        setListFile({ name: file.name, role: 'list' });
      } else if (role === 'template' || role === 'reference_bid') {
        setTemplateFile({ name: file.name });
      } else if (role === 'writing_material') {
        setWritingMaterialFile({ name: file.name });
      } else if (role === 'drawing') {
        setDrawingFile({ name: file.name });
      }
      setMessage('文件上传成功');
    } catch (error) {
      setMessage(getErrorMessage(error, '文件上传失败'));
    } finally {
      setUploadingFile(false);
    }
  };

  const saveProjectSetup = async (
    form: ProjectFormState,
    referenceFile: File | null
  ) => {
    setIsSaving(true);
    setMessage('');
    try {
      const response = await projectApi.create({
        project_id: projectId || undefined,
        project_name: form.projectName.trim(),
        expected_total_words: form.expectedTotalWords,
      });
      const activeProjectId = response.data.data.project_id;
      
      if (!projectId) {
        setProjectId(activeProjectId);
        draftStorage.migrateToProject(activeProjectId);
        onProjectCreated({
          id: activeProjectId,
          project_name: form.projectName.trim(),
          overview: '',
          requirements: '',
          status: 0,
          flow_status: 0,
          create_time: null,
          update_time: null,
        });
      }

      // 保存前获取最新版本号，避免版本冲突
      let currentVersionNo = form.versionNo;
      if (activeProjectId) {
        try {
          const detailResp = await projectApi.detail(activeProjectId);
          const latestVersion = detailResp.data?.data?.version_no;
          if (latestVersion !== undefined && latestVersion !== null) {
            currentVersionNo = latestVersion;
          }
        } catch {
          // 获取失败时使用原版本号
        }
      }

      const settingsResponse = await projectApi.updateSettingsDetail(activeProjectId, {
        base_version_no: currentVersionNo,
        expected_total_words: form.expectedTotalWords,
        tender_type: form.tender_type,
        writing_style: form.writing_style,
        table_density: form.table_density,
        company_name: form.company_name,
        company_intro: form.company_intro,
        company_industry: form.company_industry,
        bid_focus: form.bid_focus,
        company_scale: form.company_scale,
        layout_style: form.layout_style,
        supplier_role: form.supplier_role,
        planned_duration: form.planned_duration,
        plan_duration_days: form.plan_duration_days
          ? parseInt(form.plan_duration_days, 10)
          : null,
        plan_duration_text: form.plan_duration_text || null,
        ...(form.expected_pages > 0
          ? { expected_pages: form.expected_pages }
          : {}),
      });
      if (referenceFile) {
        const response = await expandApi.uploadExpandFile(
          referenceFile,
          activeProjectId
        );
        rememberReference(referenceFile.name, response.data.data);
      }
      const newVersion = settingsResponse.data.data.version_no;
      setSettings({
        ...form,
        versionNo: newVersion,
      });
      setMessage('项目配置保存成功');
      moveToStep(1, activeProjectId);
      return newVersion;
    } catch (requestError) {
      setMessage(getErrorMessage(requestError, '项目配置保存失败'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveAnalysisAndContinue = async () => {
    if (!state.projectOverview.trim() || !state.techRequirements.trim()) {
      setMessage('请先完成项目概述和技术评分要求');
      return;
    }
    setMessage('');
    if (projectId) {
      try {
        await projectApi.update(projectId, {
          overview: state.projectOverview,
          requirements: state.techRequirements,
        });
        await projectApi.updateFlowStatus(projectId, 20);
      } catch (requestError) {
        setMessage(getErrorMessage(requestError, '解析结果保存失败'));
        return;
      }
    }
    moveToStep(2);
  };

  const saveStep2Settings = async () => {
    if (!projectId) return;
    try {
      // 保存前获取最新版本号，避免版本冲突
      let currentVersionNo = settings.versionNo;
      try {
        const detailResp = await projectApi.detail(projectId);
        const latestVersion = detailResp.data?.data?.version_no;
        if (latestVersion !== undefined && latestVersion !== null) {
          currentVersionNo = latestVersion;
        }
      } catch {
        // 获取失败时使用原版本号
      }

      await projectApi.updateSettingsDetail(projectId, {
        base_version_no: currentVersionNo,
        expected_total_words: settings.expectedTotalWords,
        tender_type: settings.tender_type,
        writing_style: settings.writing_style,
        table_density: settings.table_density,
        company_name: settings.company_name,
        company_intro: settings.company_intro,
        company_industry: settings.company_industry,
        bid_focus: settings.bid_focus,
        company_scale: settings.company_scale,
        expected_pages: settings.expected_pages,
        layout_style: settings.layout_style,
        supplier_role: settings.supplier_role,
        planned_duration: settings.planned_duration,
        plan_duration_days: settings.plan_duration_days
          ? parseInt(settings.plan_duration_days, 10)
          : null,
        plan_duration_text: settings.plan_duration_text,
      });
    } catch (error) {
      console.error('Failed to save step 2 settings:', error);
    }
  };

  const continueAfterConfirmedAnalysis = async () => {
    if (confirmedAnalysisVersion <= 0) {
      setMessage('请先确认当前解析结果');
      return;
    }
    if (continueInFlightRef.current) return; // 防重复点击，避免连续 409
    continueInFlightRef.current = true;
    setMessage('');
    try {
      await saveStep2Settings();
      moveToStep(2);
    } finally {
      continueInFlightRef.current = false;
    }
  };

  // Retained until removal of the legacy analysis flow is explicitly approved.
  void saveAnalysisAndContinue;

  const saveOutlineAndContinue = async () => {
    if (!state.outlineData?.outline.length) {
      setMessage('请先生成标书目录');
      return;
    }
    setMessage('');
    if (projectId) {
      try {
        await projectApi.saveOutline(projectId, state.outlineData.outline);
        await projectApi.calculateWordAllocation(
          projectId,
          settings.expectedTotalWords
        );
        const outlineResponse = await projectApi.getOutline(projectId);
        updateOutline({
          ...state.outlineData,
          outline: outlineResponse.data.data,
        });
      } catch (requestError) {
        setMessage(getErrorMessage(requestError, '目录保存失败'));
        return;
      }
    }
    moveToStep(3);
  };

  // Retained for potential future use in outline editing flow.
  void saveOutlineAndContinue;

  useEffect(() => {
    // 步骤已简化为4步，无需特殊的货物类型跳转逻辑
  }, [
    currentStep,
    isLoading,
    onRouteChange,
    projectId,
    settings.tender_type,
  ]);

  const outlineTitles = useMemo(() => {
    const collect = (items: OutlineItem[]): string[] =>
      items.flatMap((item) => [
        `${item.id} ${item.title}`,
        ...(item.children ? collect(item.children) : []),
      ]);
    return state.outlineData ? collect(state.outlineData.outline) : [];
  }, [state.outlineData]);
  const visibleSteps = STEPS;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl">
        <div className="h-20 animate-pulse rounded-2xl bg-white" />
        <div className="mt-6 h-[520px] animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1380px]">
      <div className="mb-6 flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[var(--bid-color-primary)]">
          <ArrowLeftIcon className="h-4 w-4" />
          返回工作台
        </button>
        <div className="text-sm text-slate-400">
          {detail?.project_name || settings.projectName || '新建标书'}
        </div>
      </div>

      <div className="mb-7 rounded-2xl border border-slate-100 bg-white px-8 py-6 shadow-[0_8px_30px_rgba(31,52,86,0.04)]">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${visibleSteps.length}, minmax(0, 1fr))`,
          }}
        >
          {visibleSteps.map((step, visibleIndex) => {
            const completed = step.step < currentStep;
            const active = step.step === currentStep;
            return (
              <button
                key={step.title}
                type="button"
                onClick={() => step.step <= currentStep && moveToStep(step.step)}
                className="relative flex items-start text-left"
              >
                {visibleIndex < visibleSteps.length - 1 && (
                  <span className={`absolute left-10 right-0 top-5 h-px ${completed ? 'bg-[var(--bid-color-primary)]' : 'bg-slate-200'}`} />
                )}
                <span className={`relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 text-sm font-semibold ${
                  completed
                    ? 'border-[var(--bid-color-primary)] bg-[var(--bid-color-primary)] text-white'
                    : active
                      ? 'border-[var(--bid-color-primary)] bg-[var(--bid-color-primary)] text-white'
                      : 'border-slate-200 bg-white text-slate-400'
                }`}>
                  {completed ? <CheckIcon className="h-5 w-5" /> : visibleIndex + 1}
                </span>
                <span className="relative z-10 ml-3 bg-white pr-5">
                  <span className={`block text-sm font-medium ${active || completed ? 'text-slate-800' : 'text-slate-400'}`}>
                    {step.title}
                  </span>
                  <span className="mt-1 block text-xs text-slate-400">{step.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {currentStep === 0 && (
        <ProjectSetup
          initial={settings}
          projectId={projectId}
          referenceName={referenceMaterial?.fileName || ''}
          isSaving={isSaving}
          message={message}
          onReferenceReady={setReferenceMaterial}
          onSave={saveProjectSetup}
        />
      )}

      {currentStep === 1 && (
        <div>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">招标文件解读</h2>
              <p className="mt-2 text-sm text-slate-500">解析招标文件，提取项目概述和技术评分要求。</p>
            </div>
          </div>

          <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(31,52,86,0.04)]">
            <div className="mb-4">
              <FieldLabel required>招标文件解析</FieldLabel>
              <p className="mb-3 text-xs text-slate-400">以下两种方式任选一种</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => tenderDocFileRef.current?.click()}
                  className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-[var(--bid-color-primary)] bg-[var(--bid-color-primary-soft)] px-6 py-4 text-center"
                >
                  <DocumentArrowUpIcon className="h-6 w-6 text-[var(--bid-color-primary)]" />
                  <div className="text-sm font-medium text-[var(--bid-color-primary)]">上传招标文件</div>
                  <div className="text-xs text-slate-500">上传并自动解析招标文件，总结项目概述、评分要求</div>
                  <div className="text-xs text-slate-400">文件格式:doc、docx、pdf（不支持扫描版pdf）</div>
                </button>
                <button
                  type="button"
                  onClick={() => setShowTechRequirementsModal(true)}
                  className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-slate-200 bg-white px-6 py-4 text-center hover:border-[var(--bid-color-primary-border)]"
                >
                  <ClipboardIcon className="h-6 w-6 text-slate-400" />
                  <div className="text-sm font-medium text-slate-700">粘贴项目信息</div>
                  <div className="text-xs text-slate-500">手动粘贴并保存项目概述、评分要求</div>
                </button>
                <input
                  ref={tenderDocFileRef}
                  type="file"
                  accept=".doc,.docx,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      uploadStep2File(file, 'reference_bid');
                    }
                    e.target.value = '';
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              <FieldLabel required>标书类型</FieldLabel>
              <SegmentedOptions
                value={settings.tender_type || '服务类'}
                options={['服务类', '货物类', '工程类', '其他']}
                onChange={(value) => setSettings({ ...settings, tender_type: value })}
                columns={4}
              />
            </div>

            {settings.tender_type === '货物类' && (
              <div className="mb-4">
                <FieldLabel>用户身份</FieldLabel>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="supplier_role"
                      value="经销商"
                      checked={settings.supplier_role === '经销商' || !settings.supplier_role}
                      onChange={(e) => setSettings({ ...settings, supplier_role: e.target.value })}
                      className="h-4 w-4 text-[var(--bid-color-primary)]"
                    />
                    <span className="text-sm">我是经销商</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="supplier_role"
                      value="生产商"
                      checked={settings.supplier_role === '生产商'}
                      onChange={(e) => setSettings({ ...settings, supplier_role: e.target.value })}
                      className="h-4 w-4 text-[var(--bid-color-primary)]"
                    />
                    <span className="text-sm">我是生产商</span>
                  </label>
                </div>
              </div>
            )}

            {settings.tender_type === '工程类' && (
              <div className="mb-4">
                <FieldLabel>计划工期</FieldLabel>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="planned_duration_type"
                      checked={settings.planned_duration !== '招标文件未提供'}
                      onChange={() => setSettings({ ...settings, planned_duration: '' })}
                      className="h-4 w-4 text-[var(--bid-color-primary)]"
                    />
                    <span className="text-sm">计划工期</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={settings.plan_duration_days || ''}
                    onChange={(e) => setSettings({ ...settings, plan_duration_days: e.target.value })}
                    placeholder="请输入"
                    className="h-10 w-32 rounded-lg border border-slate-200 px-3 text-sm"
                  />
                  <span className="text-sm text-slate-500">天</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="planned_duration_type"
                      checked={settings.planned_duration === '招标文件未提供'}
                      onChange={() => setSettings({ ...settings, planned_duration: '招标文件未提供' })}
                      className="h-4 w-4 text-[var(--bid-color-primary)]"
                    />
                    <span className="text-sm">招标文件未提供</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* 可选资料 - 折叠展开 */}
          <div className="mb-6 rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(31,52,86,0.04)]">
            <button
              type="button"
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              className="flex w-full items-center justify-between p-5 text-left"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600">
                  <LightBulbIcon className="h-4 w-4" />
                </span>
                <div>
                  <h3 className="font-semibold text-slate-900">可选资料</h3>
                  <p className="mt-1 text-xs text-slate-400">上传额外资料，帮助 AI 生成更精准的内容</p>
                </div>
              </div>
              <ChevronRightIcon className={`h-5 w-5 text-slate-400 transition-transform ${showMoreOptions ? 'rotate-90' : ''}`} />
            </button>

            {showMoreOptions && (
              <div className="border-t border-slate-100 p-6 space-y-5">
                {/* 清单文件 */}
                <div>
                  <FieldLabel>{getListTitle(settings.tender_type)}</FieldLabel>
                  <UploadCard
                    title={getListTitle(settings.tender_type)}
                    description=""
                    uploadHint={`上传${getListTitle(settings.tender_type)}（可选）`}
                    pasteHint={`粘贴${getListTitle(settings.tender_type)}`}
                    acceptFormats="文件格式:doc、docx、pdf、xlsx、xls"
                    maxSize="5MB"
                    fileName={listFile?.name}
                    isUploading={uploadingFile}
                    uploadMode={listUploadMode}
                    onModeChange={setListUploadMode}
                    showPaste={true}
                    onUpload={(file) => uploadStep2File(file, 'list')}
                    onPaste={(text) => {
                      setListPasteText(text);
                      setListFile({ name: '粘贴的清单', role: 'list' });
                      void saveListToBackend(text);
                    }}
                    onBlur={(text) => {
                      setListPasteText(text);
                      setListFile({ name: '粘贴的清单', role: 'list' });
                      void saveListToBackend(text);
                    }}
                  />
                  {listUploadMode === 'paste' && listFile && (
                    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                      清单已保存到系统，可继续下一步操作
                    </div>
                  )}
                </div>

                {/* 工程图纸 - 仅工程类 */}
                {settings.tender_type === '工程类' && (
                  <div>
                    <FieldLabel>工程图纸</FieldLabel>
                    <p className="mb-3 text-xs text-slate-400">AI识别图纸信息，撰写更精准的施工方案（可选）</p>
                    <UploadCard
                      title="工程图纸"
                      description=""
                      uploadHint="上传工程图纸"
                      acceptFormats="文件格式:pdf（可将工程图纸图片转换成PDF文件）暂不支持DWG格式"
                      maxSize="100MB"
                      fileName={drawingFile?.name}
                      isUploading={uploadingFile}
                      showPaste={false}
                      onUpload={(file) => uploadStep2File(file, 'drawing')}
                    />
                  </div>
                )}

                {/* 参考资料 */}
                <div>
                  <FieldLabel>写标参考资料</FieldLabel>
                  <div className="grid grid-cols-2 gap-3">
                    <UploadCard
                      title="标书范本"
                      description=""
                      uploadHint="参考历史标书的目录结构、章节写法和方案（可选）"
                      fileName={templateFile?.name}
                      isUploading={uploadingFile}
                      showPaste={false}
                      onUpload={(file) => uploadStep2File(file, 'template')}
                    />
                    <UploadCard
                      title="写作资料"
                      description=""
                      uploadHint="参考制度、案例、资质等资料，补充正文（可选）"
                      fileName={writingMaterialFile?.name}
                      isUploading={uploadingFile}
                      showPaste={false}
                      onUpload={(file) => uploadStep2File(file, 'writing_material')}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DocumentAnalysis
            projectId={projectId}
            fileContent={state.fileContent}
            projectFiles={detail?.files.tender || []}
            projectOverview={state.projectOverview}
            techRequirements={state.techRequirements}
            onFileUpload={saveUploadedFile}
            onFileDeleted={removeProjectFile}
            onAnalysisComplete={updateAnalysisResults}
            onConfirmed={setConfirmedAnalysisVersion}
          />
          {message && <div className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{message}</div>}
          <div className="mt-7 flex justify-between">
            <button onClick={() => moveToStep(0)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm text-slate-600">上一步</button>
            <button disabled={confirmedAnalysisVersion <= 0} onClick={continueAfterConfirmedAnalysis} className="inline-flex items-center gap-2 rounded-xl bg-[var(--bid-color-primary)] px-6 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300">
              下一步
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        projectId ? (
          <DurableOutlineWorkspace
            projectId={projectId}
            onConfirmed={() => moveToStep(3)}
          />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
            <ArrowPathIcon className="mx-auto mb-3 h-6 w-6 animate-spin" />
            正在加载项目信息...
          </div>
        )
      )}

      {currentStep === 3 && (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-slate-900">生成正文</h2>
            <p className="mt-2 text-sm text-slate-500">按实际章节状态生成正文，完成后导出可编辑文档。</p>
          </div>
          {projectId ? (
            <ContentEdit
              projectId={projectId}
              projectName={detail?.project_name || settings.projectName}
            />
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
              <ArrowPathIcon className="mx-auto mb-3 h-6 w-6 animate-spin" />
              正在加载项目信息...
            </div>
          )}
          <div className="mt-7">
            <button onClick={() => moveToStep(2)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm text-slate-600">返回目录编辑</button>
          </div>
        </div>
      )}

      {aiModal && state.outlineData && (
        <AiDirectoryModal
          type={aiModal}
          outlineTitles={outlineTitles}
          outlineData={state.outlineData}
          projectOverview={state.projectOverview}
          techRequirements={state.techRequirements}
          onAdopt={(optimizedOutline) => {
            updateOutline(optimizedOutline);
            setAiModal(null);
            setMessage('目录优化结果已采用，请保存目录后继续');
          }}
          onClose={() => setAiModal(null)}
        />
      )}

      {showTechRequirementsModal && (
        <TechRequirementsModal
          isOpen={showTechRequirementsModal}
          onClose={() => setShowTechRequirementsModal(false)}
          overview={state.projectOverview}
          requirements={state.techRequirements}
          onSave={async (newOverview, newRequirements) => {
            if (projectId === null || projectId === undefined) {
              setMessage('项目信息加载中，请稍候再试');
              return;
            }
            setIsSaving(true);
            setMessage('');
            try {
              updateAnalysisResults(newOverview, newRequirements);
              // 保存前先获取最新的分析版本号，避免版本冲突
              let baseAnalysisVersion = 0;
              try {
                const analysisResp = await analysisApi.get(projectId);
                const latestAnalysis = analysisResp.data?.data;
                if (latestAnalysis?.analysis) {
                  baseAnalysisVersion = latestAnalysis.analysis.version_no;
                }
              } catch {
                // 获取失败时使用 detail 中的版本号
                baseAnalysisVersion = detail?.analysis_version ?? 0;
              }
              
              const updateResp = await analysisApi.update(projectId, {
                base_analysis_version: baseAnalysisVersion,
                overview: newOverview.trim(),
                requirements: newRequirements,
                analysis_mode: 'paste',
              });
              const savedSnapshot = updateResp.data.data;
              const savedVersion = savedSnapshot.version_no;
              // 确认解析，解锁"保存并生成目录"按钮
              const confirmResp = await analysisApi.confirm(projectId, savedVersion);
              const confirmedVersion = confirmResp.data.data.version_no;
              updateAnalysisResults(
                confirmResp.data.data.overview || newOverview,
                confirmResp.data.data.requirements || newRequirements
              );
              setConfirmedAnalysisVersion(confirmedVersion);
              // 同步刷新 detail 的版本号
              setDetail((prev) =>
                prev ? { ...prev, analysis_version: confirmedVersion } : prev
              );
              // 重新加载项目详情，确保 version_no 等字段是最新的
              try {
                const freshDetailResp = await projectApi.detail(projectId);
                const freshDetail = freshDetailResp.data.data;
                setDetail(freshDetail);
                if (freshDetail?.version_no !== undefined && freshDetail?.version_no !== null) {
                  setSettings((prev) => ({ ...prev, versionNo: freshDetail.version_no! }));
                }
              } catch {
                // 忽略刷新失败
              }
              setMessage('技术评分要求已保存并确认');
            } catch (error) {
              console.error('TechRequirementsModal save failed:', error);
              setMessage(getErrorMessage(error, '技术评分要求保存失败'));
            } finally {
              setIsSaving(false);
              setShowTechRequirementsModal(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default WorkflowStudio;